import { type FC, useState } from "react";

import styled from "styled-components";

import { Button, PlainTooltip } from "@similarweb/ui-components";

import type { IInitialKeywordGroup, IUserKeywordGroup } from "app/services/keyword-groups";
import { KEYWORD_GROUP_PREFIX } from "app/services/keyword-groups";
import { useCreateOrUpdateKeywordGroup } from "app/services/keyword-groups/hooks";
import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { BasicDurations } from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { useKeywordGroupNameContext } from "../KeywordGroupNameContext";
import { useKeywordListComposerContext } from "../KeywordListComposerContext";

const useFooter = () => {
    const { suggestionsStatus, keywords, country } = useKeywordListComposerContext();
    const { keywordGroupName, keywordGroupNameValidationStatus } = useKeywordGroupNameContext();
    const [isCreatingGroup, setIsCreatingGroup] = useState(false);
    const createOrUpdateKeywordGroup = useCreateOrUpdateKeywordGroup();
    const { isValid } = keywordGroupNameValidationStatus;
    const keywordsCount = keywords.length;
    const isEmptyGroup = keywordsCount === 0;
    const errorMessage =
        "errorMessage" in keywordGroupNameValidationStatus
            ? keywordGroupNameValidationStatus.errorMessage
            : "Add keywords first";
    const isDisabled = suggestionsStatus === "pending" || !isValid || isEmptyGroup;
    const onSubmit = () => {
        const customAttributes = {
            keywordGroupName,
            keywordsCount,
        };
        allTrackers.dispatchTrackEvent("Pop Up", "click", "save", customAttributes);
        setIsCreatingGroup(true);
        const keywordsGroup: IInitialKeywordGroup = {
            Name: keywordGroupName,
            Keywords: keywords.map(({ term }) => term),
        };
        createOrUpdateKeywordGroup
            .mutateAsync(keywordsGroup)
            .then((newGroup: IUserKeywordGroup) => {
                if (newGroup) {
                    const targetState = isAvailable(swSettings.components.DemandSearchTrends)
                        ? "digitalsuite_marketresearch_keywordmarketanalysis_demand_search_trends"
                        : "digitalsuite_marketresearch_keywordmarketanalysis_total";

                    const { Id } = newGroup;
                    const params = {
                        keyword: KEYWORD_GROUP_PREFIX + Id,
                        country: country.id,
                        duration: BasicDurations.LAST_TWELVE_MONTHS,
                        ignoreFilterConsistency: true,
                    };
                    proNavigator.go(targetState, params);
                }
            })
            .finally(() => {
                setIsCreatingGroup(false);
            });
    };
    return { errorMessage, isDisabled, onSubmit, isCreatingGroup };
};

const Submit = ({ errorMessage, isDisabled, onSubmit, isCreatingGroup }) => {
    return (
        <PlainTooltip
            tooltipContent={translate(errorMessage)}
            enabled={isDisabled}
        >
            <div>
                <Button
                    type="primary"
                    onClick={onSubmit}
                    isDisabled={isDisabled}
                    isLoading={isCreatingGroup}
                >
                    {translate("auto.keyword.list.generator.footer.submit")}
                </Button>
            </div>
        </PlainTooltip>
    );
};

interface FooterProps {
    onClose: VoidFunction;
}

export const Footer: FC<FooterProps> = ({ onClose }) => {
    const { errorMessage, isDisabled, onSubmit, isCreatingGroup } = useFooter();
    return (
        <FlexRow
            justifyContent="flex-end"
            gap="8px"
        >
            <Button
                type="flatDark"
                onClick={onClose}
            >
                {translate("common.close")}
            </Button>
            <Submit
                errorMessage={errorMessage}
                isDisabled={isDisabled}
                onSubmit={onSubmit}
                isCreatingGroup={isCreatingGroup}
            />
        </FlexRow>
    );
};

const Container = styled(FlexRow)`
    margin-left: -16px;
`;

export const PreviewFooter: FC<FooterProps> = ({ onClose }) => {
    const { changeMode } = useKeywordListComposerContext();
    const { errorMessage, isDisabled, onSubmit, isCreatingGroup } = useFooter();

    const handleEditButtonClick = () => {
        changeMode("edit_existing_list");
        allTrackers.dispatchTrackEvent("Pop Up", "click", "advanced editing");
    };

    return (
        <Container
            justifyContent="space-between"
            gap="8px"
        >
            <Button
                type="flatDark"
                onClick={onClose}
            >
                {translate("common.close")}
            </Button>
            <FlexRow gap="8px">
                <Button
                    type="flat"
                    onClick={handleEditButtonClick}
                >
                    {translate("auto.keyword.list.generator.footer.preview.edit")}
                </Button>
                <Submit
                    errorMessage={errorMessage}
                    isDisabled={isDisabled}
                    onSubmit={onSubmit}
                    isCreatingGroup={isCreatingGroup}
                />
            </FlexRow>
        </Container>
    );
};
